import React, { useState, useCallback, useEffect } from 'react'
import { refreshBillingAfterPayment } from '../refreshBilling'
import {
  DialogRoot as Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils'
import { toast } from '@/components/ui/toast'
import { walletAPI } from '@/lib/api/wallet'
import { getStripe } from '@/lib/stripe'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'

// ============================================================================
// CONSTANTS
// ============================================================================

const AMOUNT_PRESETS = [10, 25, 50, 100] as const

const formatUSD = (value: number): string =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/**
 * Maps Stripe error codes to user-friendly messages.
 * Stripe.js returns these via confirmPayment() or elements.submit().
 */
function getPaymentErrorMessage(error: {
  type?: string
  code?: string
  decline_code?: string
  message?: string
}): string {
  // Handle specific decline codes first
  if (error.decline_code) {
    const declineMessages: Record<string, string> = {
      insufficient_funds: 'Your card has insufficient funds. Please try another card.',
      lost_card: 'This card has been reported lost. Please use a different card.',
      stolen_card: 'This card has been reported stolen. Please use a different card.',
      generic_decline: 'Your card was declined. Please try another card or contact your bank.',
      do_not_honor: 'Your card was declined. Please contact your bank for details.',
      fraudulent: 'This payment was flagged as suspicious. Please contact your bank.',
    }
    if (declineMessages[error.decline_code]) return declineMessages[error.decline_code]
  }

  // Handle error codes
  if (error.code) {
    const codeMessages: Record<string, string> = {
      card_declined: 'Your card was declined. Please try another payment method.',
      expired_card: 'Your card has expired. Please use a different card.',
      incorrect_cvc: 'The security code (CVC) is incorrect. Please check and try again.',
      incorrect_number: 'The card number is incorrect. Please check and try again.',
      processing_error:
        'An error occurred while processing your card. Please try again in a moment.',
      authentication_required:
        'Additional authentication is required. Please complete the verification.',
      payment_intent_authentication_failure:
        'Authentication failed. Please try again or use a different payment method.',
    }
    if (codeMessages[error.code]) return codeMessages[error.code]
  }

  // Fallback to Stripe's message or generic
  return error.message ?? 'Payment failed. Please try again.'
}

// ============================================================================
// STRIPE PAYMENT FORM (inside Elements provider)
// ============================================================================

interface PaymentFormProps {
  amount: number
  currentBalance: number | null
  onSuccess: () => void
  onCancel: () => void
}

function PaymentForm({ amount, currentBalance, onSuccess, onCancel }: PaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!stripe || !elements) return

      setIsProcessing(true)
      setError(null)

      try {
        const { error: submitError } = await elements.submit()
        if (submitError) {
          setError(getPaymentErrorMessage(submitError))
          setIsProcessing(false)
          return
        }

        // confirmPayment with redirect: 'if_required' handles 3DS inline.
        // Stripe.js opens a modal for 3DS authentication automatically.
        // While the modal is open, isProcessing stays true (shows spinner).
        const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}${window.location.pathname}?topup=success`,
          },
          redirect: 'if_required',
        })

        if (confirmError) {
          // User may have closed the 3DS modal or authentication failed
          setError(getPaymentErrorMessage(confirmError))
          setIsProcessing(false)
          return
        }

        // Check the PaymentIntent status — credits are only added via webhook
        // on payment_intent.succeeded, so we just need to confirm the intent
        // reached a terminal or processing state.
        if (paymentIntent) {
          switch (paymentIntent.status) {
            case 'succeeded':
            case 'processing':
              // Payment completed or being processed asynchronously.
              // The webhook will credit the wallet when it succeeds.
              onSuccess()
              return

            case 'requires_action':
              // 3DS was required but not completed (rare with redirect: 'if_required')
              setError('Additional authentication is required. Please try again.')
              setIsProcessing(false)
              return

            case 'requires_payment_method':
              // Payment method failed — user needs to try a different card
              setError('Payment failed. Please try a different payment method.')
              setIsProcessing(false)
              return

            default:
              setError('Payment could not be completed. Please try again.')
              setIsProcessing(false)
              return
          }
        }

        setError('Payment status is unavailable. Check your billing history before trying again.')
        setIsProcessing(false)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Something went wrong')
        setIsProcessing(false)
      }
    },
    [stripe, elements, onSuccess]
  )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Summary */}
      <div className="bg-muted/50 flex flex-col gap-2 rounded-lg p-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Current balance</span>
          <span>{currentBalance === null ? 'Unavailable' : formatUSD(currentBalance)}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Top-up amount</span>
          <span className="font-medium">{formatUSD(amount)}</span>
        </div>
        <div className="flex items-center justify-between border-t border-border pt-2 text-sm font-medium">
          <span>Balance after payment</span>
          <span>
            {currentBalance === null ? 'Updates after payment' : formatUSD(currentBalance + amount)}
          </span>
        </div>
      </div>

      {/* Stripe PaymentElement */}
      <div className="min-h-[120px]">
        <PaymentElement onReady={() => setReady(true)} options={{ layout: 'tabs' }} />
      </div>

      {/* Error */}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onCancel}
          disabled={isProcessing}
        >
          Back
        </Button>
        <Button type="submit" className="flex-1" disabled={!stripe || !ready || isProcessing}>
          {isProcessing ? (
            <>
              <Icon name="loader-2" className="mr-1.5 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            `Pay ${formatUSD(amount)}`
          )}
        </Button>
      </div>
    </form>
  )
}

// ============================================================================
// MAIN DIALOG
// ============================================================================

interface TopUpDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentBalance: number | null
  /** Optional initial amount (e.g. from billing tier selection) */
  initialAmount?: number
}

export function TopUpDialog({
  open,
  onOpenChange,
  currentBalance,
  initialAmount,
}: TopUpDialogProps) {
  const [amount, setAmount] = useState(initialAmount ?? 50)
  const [customAmount, setCustomAmount] = useState('')
  const [isCustom, setIsCustom] = useState(false)
  const [step, setStep] = useState<'amount' | 'payment'>('amount')
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [isCreatingIntent, setIsCreatingIntent] = useState(false)
  const [intentError, setIntentError] = useState<string | null>(null)
  const [stripePromise] = useState(() => getStripe())

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep('amount')
      setClientSecret(null)
      setIntentError(null)
      setIsCreatingIntent(false)
    }
  }, [open])

  // Update amount when initialAmount changes (e.g. tier selection)
  useEffect(() => {
    if (initialAmount && initialAmount > 0) {
      setAmount(initialAmount)
      const preset = AMOUNT_PRESETS.some((amount) => amount === initialAmount)
      setIsCustom(!preset)
      setCustomAmount(preset ? '' : String(initialAmount))
    }
  }, [initialAmount])

  const effectiveAmount = isCustom ? parseFloat(customAmount) || 0 : amount
  const isValidAmount = effectiveAmount >= 1 && effectiveAmount <= 1000

  const handlePresetClick = (preset: number) => {
    setAmount(preset)
    setIsCustom(false)
    setCustomAmount('')
  }

  const handleCustomChange = (value: string) => {
    // Allow digits and single decimal point, max 2 decimal places
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setCustomAmount(value)
      setIsCustom(true)
    }
  }

  const handleProceed = useCallback(async () => {
    if (!isValidAmount) return

    setIsCreatingIntent(true)
    setIntentError(null)

    try {
      const stripe = await stripePromise
      if (!stripe) throw new Error('Payments are currently unavailable. Please try again later.')
      const result = await walletAPI.createTopUp(effectiveAmount, true)
      if (!result.clientSecret)
        throw new Error('Payment could not be initialized. Please try again.')
      setClientSecret(result.clientSecret)
      setStep('payment')
    } catch (err) {
      setIntentError(err instanceof Error ? err.message : 'Failed to initialize payment')
    } finally {
      setIsCreatingIntent(false)
    }
  }, [effectiveAmount, isValidAmount, stripePromise])

  const handleSuccess = useCallback(() => {
    onOpenChange(false)
    toast('Payment submitted. Credits will appear once payment is confirmed.')
    refreshBillingAfterPayment()
  }, [onOpenChange])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] overflow-y-auto sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{step === 'amount' ? 'Add credits' : 'Complete payment'}</DialogTitle>
          <DialogDescription className="sr-only">
            Add funds to your in-app balance
          </DialogDescription>
        </DialogHeader>

        {step === 'amount' ? (
          <div className="flex flex-col gap-4">
            {/* Current balance */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="wallet" className="h-4 w-4" />
              <span>
                Current balance:{' '}
                <span className="font-medium text-foreground">
                  {currentBalance === null ? 'Unavailable' : formatUSD(currentBalance)}
                </span>
              </span>
            </div>

            {/* Amount presets */}
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">Choose amount</p>
              <div className="grid grid-cols-4 gap-2">
                {AMOUNT_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => handlePresetClick(preset)}
                    className={cn(
                      'flex items-center justify-center rounded-lg border-2 py-2.5 text-sm font-medium transition-all',
                      !isCustom && amount === preset
                        ? 'bg-primary/5 border-primary text-foreground'
                        : 'hover:border-primary/50 border-border bg-muted text-foreground'
                    )}
                  >
                    ${preset}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom amount */}
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">Or enter custom amount</p>
              <div
                className={cn(
                  'flex items-center rounded-lg border-2 px-3 py-2 transition-all',
                  isCustom ? 'bg-primary/5 border-primary' : 'border-border bg-muted'
                )}
              >
                <span className="mr-1 text-sm text-muted-foreground">$</span>
                <input
                  aria-label="Custom top-up amount"
                  type="text"
                  inputMode="decimal"
                  value={customAmount}
                  onChange={(e) => handleCustomChange(e.target.value)}
                  onFocus={() => setIsCustom(true)}
                  placeholder="0.00"
                  className="flex-1 bg-transparent text-sm font-medium outline-none placeholder:text-muted-foreground"
                />
              </div>
              <p className="text-[11px] text-muted-foreground">Min $1.00 — Max $1,000.00</p>
            </div>

            {/* Error */}
            {intentError && <p className="text-sm text-destructive">{intentError}</p>}

            {/* Proceed button */}
            <Button
              size="lg"
              className="w-full"
              onClick={handleProceed}
              disabled={!isValidAmount || isCreatingIntent}
            >
              {isCreatingIntent ? (
                <>
                  <Icon name="loader-2" className="mr-1.5 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                `Proceed to payment — ${formatUSD(effectiveAmount)}`
              )}
            </Button>
          </div>
        ) : clientSecret && stripePromise ? (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              // Elements renders in an iframe, so the page's font has to be
              // loaded into it explicitly — 'inherit' has nothing to inherit
              // from there and falls back to the browser's default serif.
              fonts: [
                {
                  cssSrc:
                    'https://fonts.googleapis.com/css2?family=Geist:wght@100..900&display=swap',
                },
              ],
              appearance: {
                theme: 'stripe',
                variables: {
                  borderRadius: '8px',
                  fontFamily: "'Geist', system-ui, sans-serif",
                },
              },
            }}
          >
            <PaymentForm
              amount={effectiveAmount}
              currentBalance={currentBalance}
              onSuccess={handleSuccess}
              onCancel={() => setStep('amount')}
            />
          </Elements>
        ) : (
          <div className="flex items-center justify-center py-8">
            <Icon name="loader-2" className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
